import { format, parseISO, isValid } from 'date-fns';

export const formatCurrency = (amount: number | null | undefined, currency = 'USD', showZero = true): string => {
  if (amount === null || amount === undefined) {
    return '';
  }

  // Don't show zero values if showZero is false
  if (!showZero && amount === 0) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

export const formatNumber = (num: number | null | undefined, decimals = 2, showZero = true): string => {
  if (num === null || num === undefined) {
    return '';
  }

  // Don't show zero values if showZero is false
  if (!showZero && num === 0) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

export const formatDate = (date: string | Date, formatString = 'MMM dd, yyyy'): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) {
      return 'Invalid Date';
    }
    return format(dateObj, formatString);
  } catch (error) {
    return 'Invalid Date';
  }
};

export const formatDateTime = (date: string | Date): string => {
  return formatDate(date, 'MMM dd, yyyy HH:mm');
};

export const formatDateForInput = (date: string | Date): string => {
  return formatDate(date, 'yyyy-MM-dd');
};

/**
 * Audit date-only values (deposit date, forward date, import/period dates) are stored as
 * either a bare `YYYY-MM-DD` string or a UTC-midnight ISO timestamp (`YYYY-MM-DDT00:00:00.000Z`).
 * Format them as MM/DD/YYYY from their UTC calendar components — NEVER via local-time
 * conversion (`new Date(str)` + `format`), which shifts the day in negative-offset zones (ET)
 * and made audit dates render a day early / inconsistently.
 * Returns null for anything that is not an unambiguous date-only value (e.g. a real timestamp
 * with a non-midnight time), so callers fall back to their default rendering.
 */
export const formatDateOnly = (value: unknown): string | null => {
  let y: number, m: number, d: number;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    // Only a value at exact UTC midnight is treated as date-only; timestamps keep their time.
    if (value.getUTCHours() || value.getUTCMinutes() || value.getUTCSeconds() || value.getUTCMilliseconds()) {
      return null;
    }
    y = value.getUTCFullYear();
    m = value.getUTCMonth() + 1;
    d = value.getUTCDate();
  } else if (typeof value === 'string') {
    const match =
      value.match(/^(\d{4})-(\d{2})-(\d{2})$/) ||
      value.match(/^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?Z$/);
    if (!match) return null;
    y = Number(match[1]);
    m = Number(match[2]);
    d = Number(match[3]);
  } else {
    return null;
  }
  return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
};

/**
 * Render an audit change value (old/new) for display: date-only values become MM/DD/YYYY
 * (UTC calendar components), everything else is stringified. `null`/`undefined` → `dash`.
 */
export const formatAuditValue = (value: unknown, dash = '—'): string => {
  if (value === null || value === undefined) return dash;
  const dateOnly = formatDateOnly(value);
  if (dateOnly) return dateOnly;
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

export const formatPercentage = (value: number, decimals = 1): string => {
  return `${formatNumber(value, decimals)}%`;
};

export const truncateText = (text: string, maxLength = 50): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const formatFileSize = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatStatus = (status: string): string => {
  return status
    .split('_')
    .map(word => capitalizeFirst(word))
    .join(' ');
};